import { TreeProps } from "../layout";
import { DocumentBundle, DocumentRef } from "./document-types";

type DocumentTreeProps = {
  bundle: DocumentBundle;
  label?: (a: DocumentNode) => any;
};

export type DocumentNode<T = any> = {
  id: string;
  label: string;
  data?: T;
  children?: DocumentNode[];
  ref?: DocumentRef;
};

export function documentBundleTreeProps(
  props: DocumentTreeProps
): TreeProps<DocumentNode> {
  const { bundle, label } = props;
  const expanded = [];

  const nodes: DocumentNode[] = Object.entries(bundle.documents).map(
    ([id, docs]) => {
      const children: DocumentNode[] = [];

      Object.entries(docs).forEach(([category, sub]) => {
        const categories = [];

        Object.entries(sub).forEach(([k, v]) => {
          const categoryNode: DocumentNode = {
            id: category + k,
            label: category,
            children: [],
          };

          Object.values(v).forEach((v) => {
            Object.values(v).forEach((ref) => {
              expanded.push(ref.key);

              categoryNode.children.push({
                id: ref.key,
                label: category,
                data: ref,
              });
            });
          });

          expanded.push(categoryNode.id);
          categories.push(categoryNode);
        });

        children.push(...categories);
      });

      const root: DocumentNode = { id, label: id, children };
      expanded.push(id);
      return root;
    }
  );

  nodes.forEach((n) => expanded.push(n.id));

  return {
    expanded,
    id: (a) => a.id,
    label: label || ((a) => a.label),
    children: (a) => a.children,
    data: nodes as any,
  };
}

export function getFirstLeaf(n: DocumentNode): DocumentNode<DocumentRef> {
  if (!n?.children?.length) {
    return null;
  }
  let a: DocumentNode = n.children[0];

  if (!a?.children?.length) {
    return null;
  }
  a = a.children[0];
  if (!a?.data) {
    return null;
  }
  return a;
}

export function findLeaf(
  list: DocumentNode[],
  fn: (a: DocumentRef) => boolean
): DocumentNode<DocumentRef> {
  let result: DocumentNode<DocumentRef>;
  if (list?.length) {
    list.find((n) => {
      const children = n?.children;
      if (children?.length) {
        children.find((c) => {
          if (c?.children?.length) {
            result = c.children.find((v) => {
              if (v?.data && fn(v.data)) {
                return v;
              }
            });
            return result;
          }
        });
      }
      return result;
    });
  }
  return result;
}

export const isDemographics = (ref: DocumentRef) =>
  (ref?.key || "").toLowerCase().indexOf("demographics") > -1;

export const isSuperChart = (ref: DocumentRef) =>
  (ref?.key || "").toLowerCase().indexOf("superchart") > -1;

export const isHospitalistPhysical = (ref: DocumentRef) =>
  (ref?.key || "").indexOf("/Hospitalist") > -1;

export const isAutoCode = (ref: DocumentRef) =>
  (ref?.key || "").toLowerCase().indexOf("auto") > -1;

export function getFirstDocument(
  location: string,
  n: DocumentNode[]
): DocumentNode<DocumentRef> {
  const h = location.indexOf("HOSPITALIST") > -1;
  let first: DocumentNode<DocumentRef>;

  if (h) {
    first = findLeaf(n, isHospitalistPhysical);
  } else {
    first = findLeaf(n, isSuperChart);
  }

  return first || getFirstLeaf(n[0]);
}

export function getAllDocumentIds(list: DocumentNode[]): string[] {
  let result: string[] = [];
  if (list?.length) {
    list.forEach((n) => {
      n && n?.id && result.push(n.id);
      const children = n?.children;
      if (children?.length) {
        children.forEach((c) => {
          c && c?.id && result.push(c.id);
          if (c?.children?.length) {
            c.children.forEach((v) => {
              v && v?.id && result.push(v.id);
            });
          }
        });
      }
    });
  }
  return result;
}
